import { useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CONDITIONS = [
  { k: "like_new", l: "Əla" },
  { k: "good", l: "Yaxşı" },
  { k: "fair", l: "Normal" },
  { k: "poor", l: "Zədəli" },
];
const storageLabel = (gb) => (gb >= 1024 ? `${gb / 1024} TB` : `${gb} GB`);

export const QuickEstimator = ({ onEvaluate }) => {
  const [catalog, setCatalog] = useState([]);
  const [modelId, setModelId] = useState("iphone-15-pro");
  const [storage, setStorage] = useState(128);
  const [condition, setCondition] = useState("like_new");
  const [estimate, setEstimate] = useState(null);

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const model = useMemo(() => catalog.find((m) => m.model_id === modelId), [catalog, modelId]);
  const device = model?.device_type || "phone";
  const showStorage = device !== "watch";

  useEffect(() => {
    if (model && showStorage && !model.storages.includes(storage)) {
      setStorage(model.storages?.[0] ?? null);
    }
  }, [model]); // eslint-disable-line

  useEffect(() => {
    if (!modelId || !condition) return;
    if (showStorage && storage == null) return;
    const t = setTimeout(() => {
      const payload = { model_id: modelId, condition, device_type: device, sim_type: "dual" };
      if (showStorage) payload.storage = storage;
      api
        .post("/estimate", payload)
        .then((r) => setEstimate(r.data.estimate ? r.data : null))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [modelId, storage, condition, device, showStorage]);

  const evaluate = () =>
    onEvaluate?.({ model_id: modelId, model_name: model?.name || "", storage: showStorage ? storage : null, condition });

  return (
    <div className="rounded-[26px] border border-white/10 bg-[#111111]/80 backdrop-blur-xl p-7 sm:p-8" data-testid="quick-estimator">
      <h3 className="font-serif text-2xl sm:text-[26px] text-white leading-tight">Cihazınızı qiymətləndirin</h3>
      <p className="text-sm text-neutral-400 mt-2 mb-6">iPhone, iPad və ya Apple Watch seçin — təxmini satış qiymətini dərhal öyrənin.</p>

      <label className="text-xs text-neutral-400">Model</label>
      <Select value={modelId} onValueChange={setModelId}>
        <SelectTrigger data-testid="qe-model" className="mt-2 h-12 bg-[#0c0c0c] border-white/10 rounded-xl text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#111] border-white/10 text-white max-h-72">
          {catalog.map((m) => (
            <SelectItem key={m.model_id} value={m.model_id} data-testid={`qe-model-${m.model_id}`}>
              {m.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {showStorage && (
        <>
          <label className="text-xs text-neutral-400 mt-6 block">Yaddaş həcmi</label>
          <div className="grid grid-cols-4 gap-2.5 mt-2">
            {(model?.storages?.length ? model.storages : [128, 256, 512, 1024]).map((s) => (
              <button key={s} onClick={() => setStorage(s)} data-testid={`qe-storage-${s}`}
                className={`h-11 rounded-xl text-sm border transition-colors ${
                  storage === s ? "border-white/70 bg-white/[0.06] text-white" : "border-white/10 text-neutral-300 hover:border-white/30"
                }`}>
                {storageLabel(s)}
              </button>
            ))}
          </div>
        </>
      )}

      <label className="text-xs text-neutral-400 mt-6 block">Vəziyyəti</label>
      <div className="grid grid-cols-4 gap-2.5 mt-2">
        {CONDITIONS.map((c) => (
          <button key={c.k} onClick={() => setCondition(c.k)} data-testid={`qe-condition-${c.k}`}
            className={`h-11 rounded-xl text-sm border transition-colors ${
              condition === c.k ? "border-white/70 bg-white/[0.06] text-white" : "border-white/10 text-neutral-300 hover:border-white/30"
            }`}>
            {c.l}
          </button>
        ))}
      </div>

      <div className="h-px bg-white/10 my-7" />

      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs text-neutral-400 mb-1">Təxmini qiymət</div>
          <div className="font-display text-4xl sm:text-5xl font-medium text-white tracking-tight" data-testid="qe-price">
            {estimate ? estimate.estimate.toLocaleString("ru-RU") : "—"} <span className="text-neutral-500">₼</span>
          </div>
        </div>
        <button onClick={evaluate} data-testid="qe-evaluate"
          className="inline-flex items-center gap-2 px-5 py-3.5 rounded-xl bg-neutral-100 text-black text-sm font-medium hover:bg-white transition-colors">
          Qiymətləndir <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-neutral-500 mt-5">Ünvanınıza gəlib cihazı alırıq.</p>
    </div>
  );
};
